import React, { useState, useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/components/ui/use-toast";
import { DialogFooter } from "@/components/ui/dialog";
import { motion, AnimatePresence } from "framer-motion";
import { Eye, EyeOff, Loader2 } from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import { login, sendPasswordReset } from "../../store/auth/authActions";
import ForgetPassword from "../Modal/ForgetPassword";

const LoginForm = ({ onLoginSuccess, onCancel, redirectPage }) => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { isLoading } = useSelector((state) => state.auth);

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetEmail, setResetEmail] = useState("");
  const [resetLoading, setResetLoading] = useState(false);

  // Validation states
  const [emailTouched, setEmailTouched] = useState(false);
  const [passwordTouched, setPasswordTouched] = useState(false);
  const [emailError, setEmailError] = useState("");
  const [passwordError, setPasswordError] = useState("");

  // Email validation regex
  const emailRegex = /^[a-zA-Z0-9._%+\-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

  // Validate email
  useEffect(() => {
    if (emailTouched) {
      if (!email) {
        setEmailError("Email is required");
      } else if (!emailRegex.test(email)) {
        setEmailError("Please enter a valid email address");
      } else {
        setEmailError("");
      }
    }
  }, [email, emailTouched]);

  // Validate password
  useEffect(() => {
    if (passwordTouched) {
      if (!password) {
        setPasswordError("Password is required");
      } else if (password.length < 6) {
        setPasswordError("Password must be at least 6 characters");
      } else {
        setPasswordError("");
      }
    }
  }, [password, passwordTouched]);

  const handleEmailChange = useCallback(
    (e) => {
      setEmail(e.target.value);
      if (!emailTouched) setEmailTouched(true);
    },
    [emailTouched],
  );

  const handlePasswordChange = useCallback(
    (e) => {
      setPassword(e.target.value);
      if (!passwordTouched) setPasswordTouched(true);
    },
    [passwordTouched],
  );

  const handleEmailBlur = useCallback(() => {
    setEmailTouched(true);
  }, []);

  const handlePasswordBlur = useCallback(() => {
    setPasswordTouched(true);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Mark both fields as touched to show errors if any
    setEmailTouched(true);
    setPasswordTouched(true);

    if (emailError || passwordError || !email || !password) {
      toast({
        title: "Validation Error",
        description: "Please fix the errors before submitting",
        variant: "destructive",
      });
      return;
    }
    try {
      const loggedInUser = await dispatch(login({ email, password })).unwrap();

      if (loggedInUser) {
        if (redirectPage) {
          navigate(redirectPage, { replace: true });
        } else {
          if (loggedInUser?.user?.role === "seller") {
            navigate("/dashboard", { replace: true });
          } else {
            navigate("/profile", { replace: true });
          }
        }
        if (onLoginSuccess) onLoginSuccess();
      }
    } catch (err) {
      console.error("Login failed:", err);
    }
  };

  const handleResetPassword = async () => {
    if (!resetEmail.trim()) {
      toast({
        title: "Email Required",
        description: "Please enter your email address",
        variant: "destructive",
      });
      return;
    }

    if (!emailRegex.test(resetEmail)) {
      toast({
        title: "Invalid Email",
        description: "Please enter a valid email address",
        variant: "destructive",
      });
      return;
    }

    try {
      setResetLoading(true);
      const result = await dispatch(sendPasswordReset(resetEmail)).unwrap();

      if (result.success) {
        setShowForgotPassword(false);
        setResetEmail("");
        onCancel();
      }

      toast({
        title: "Reset Email Sent",
        description: "Check your inbox to reset your password.",
      });
      setShowForgotPassword(false);
      setResetEmail("");
    } catch (error) {
      console.error("Error sending reset email:", error);
    } finally {
      setResetLoading(false);
    }
  };

  return (
    <>
      <motion.form
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.3 }}
        onSubmit={handleSubmit}
        className="space-y-2"
      >
        <div className="space-y-2">
          <Label htmlFor="login-email">Email</Label>
          <Input
            id="login-email"
            type="email"
            placeholder="m@example.com"
            value={email}
            onChange={handleEmailChange}
            onBlur={handleEmailBlur}
            required
            disabled={isLoading}
          />
          <AnimatePresence>
            {emailError && (
              <motion.p
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                className="text-xs text-red-400"
              >
                {emailError}
              </motion.p>
            )}
          </AnimatePresence>
        </div>
        <div className="space-y-2">
          <Label htmlFor="login-password">Password</Label>
          <div className="relative">
            <Input
              id="login-password"
              type={showPassword ? "text" : "password"}
              value={password}
              onChange={handlePasswordChange}
              onBlur={handlePasswordBlur}
              required
              minLength={6}
              maxLength={16}
              disabled={isLoading}
              className="pr-20 px-3 py-2 rounded-md border focus:outline-none focus:ring-2 focus:ring-accent w-full"
            />
            <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-2 z-10">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-4 w-4 p-0 hover:bg-transparent"
                onClick={() => setShowPassword((prev) => !prev)}
                disabled={isLoading}
              >
                {showPassword ? (
                  <EyeOff className="h-4 w-4 text-muted-foreground" />
                ) : (
                  <Eye className="h-4 w-4 text-muted-foreground" />
                )}
              </Button>
            </div>
          </div>
          <AnimatePresence>
            {passwordError && (
              <motion.p
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                className="text-xs text-red-400"
              >
                {passwordError}
              </motion.p>
            )}
          </AnimatePresence>
        </div>

        <div className="flex justify-between items-center">
          <Button
            type="button"
            variant="link"
            onClick={() => setShowForgotPassword(true)}
            className="text-sm text-ring hover:underline p-0"
          >
            Forgot Password?
          </Button>
        </div>

        <DialogFooter className="pt-4 flex-col-reverse gap-2 sm:flex-row sm:justify-end sm:space-x-2 px-0">
          <Button
            type="button"
            variant="outline"
            onClick={onCancel}
            disabled={isLoading}
            className="w-full sm:w-auto"
          >
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={
              isLoading ||
              !!emailError ||
              !!passwordError ||
              !email.trim() ||
              !password.trim()
            }
            className="w-full sm:w-auto"
          >
            {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Login
          </Button>
        </DialogFooter>
      </motion.form>
      <ForgetPassword
        showForgotPassword={showForgotPassword}
        setShowForgotPassword={setShowForgotPassword}
        setResetEmail={setResetEmail}
        resetEmail={resetEmail}
        resetLoading={resetLoading}
        handleResetPassword={handleResetPassword}
      />
    </>
  );
};

export default LoginForm;
